import { useMemo } from 'react';
import styled from 'styled-components';
import Card from '../../../components/card/Card';
import H from '../../../components/texts/h/H';
import P from '../../../components/texts/p/P';
import Span from '../../../components/texts/span/Span';
import Label from '../../../components/texts/label/Label';
import Input from '../../../components/input/Input';
import Badge from '../../../components/badge/Badge';
import Spinner from '../../../components/Spinner/spinner';
import Table from '../../../components/table/table';
import { formatCurrency } from '../../../utils/Masks';
import { ActivoFijoLibreFormProps } from './CreditoMaximo.types';
import { useClientTheme } from '../../../context/ThemeContext';

const FieldsGrid = styled.div<{ $columns: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $columns }) => $columns}, 1fr);
  column-gap: 16px;
  row-gap: 4px;
  padding: 8px 0;
  align-items: center;
`;

const FieldValue = styled.div`
  display: flex;
  align-items: center;
  min-height: 40px;

  > * {
    margin: 0;
    min-width: 0;
  }

  > div > div {
    margin-top: 0;
  }

  input {
    width: 100%;
    box-sizing: border-box;
  }
`;

/** html2canvas (PDF de riesgo) no pinta el texto de un Badge inline-flex suelto en la celda; el wrapper flex sí. */
const CalificacionBadgeCell = styled.div`
  display: flex;
  align-items: center;
`;

const calificacionConfig =(calificacion: string | null) => {
  if (calificacion === null || calificacion === undefined) {
    return { variant: 'neutral' as const, label: 'Sin datos' };
  }
  if (calificacion.toUpperCase() === 'APTO') {
    return { variant: 'success' as const, label: calificacion };
  }
  return { variant: 'danger' as const, label: calificacion };
};

const ActivoFijoLibreForm: React.FC<ActivoFijoLibreFormProps> = ({
  activoFijo,
  revaluos,
  restricciones,
  activoFijoLibre,
  creditoMaximoUnidades,
  creditoMaximoPesos,
  unidadCalculoCode,
  calificacion,
  isRecalculating,
  onRevaluosChange,
  onRestriccionesChange,
  onBlur,
  readOnly = false,
  hideCalificacion = false,
}) => {
  const { clientTheme } = useClientTheme();
  const calificacion_ = calificacionConfig(calificacion);
  const labelUnidad = unidadCalculoCode === 'M3H' ? 'Crédito Máximo (M3)' : 'Crédito Máximo (Bolsas)';

  const readOnlyColumns = useMemo(() => {
    const columns = [
      {
        name: 'Activo Fijo',
        selector: () => (
          <Span type='3' variant='neutral'>$ {formatCurrency(activoFijo)}</Span>
        ),
        grow: 1,
      },
      {
        name: 'Revaluos',
        selector: () => (
          <Span type='3' variant='neutral'>$ {formatCurrency(parseFloat(revaluos) || 0)}</Span>
        ),
        grow: 1,
      },
      {
        name: 'Restricciones *',
        selector: () => (
          <Span type='3' variant='neutral'>$ {formatCurrency(parseFloat(restricciones) || 0)}</Span>
        ),
        grow: 1,
      },
      {
        name: 'Activo Fijo Libre',
        selector: () => (
          <Span type='3' variant='neutral'>$ {formatCurrency(activoFijoLibre)}</Span>
        ),
        grow: 1,
      },
      {
        name: labelUnidad,
        selector: () => (
          <Span type='3' variant='neutral'>{formatCurrency(creditoMaximoUnidades)}</Span>
        ),
        grow: 1,
      },
      {
        name: 'Crédito Máximo $',
        selector: () => (
          <Span type='3' variant='neutral'>$ {formatCurrency(creditoMaximoPesos)}</Span>
        ),
        grow: 1,
      },
    ];

    if (!hideCalificacion) {
      columns.push({
        name: 'Calificación',
        selector: () => (
          <CalificacionBadgeCell>
            <Badge type='text' variant={calificacion_.variant} size='sm'>
              {calificacion_.label}
            </Badge>
          </CalificacionBadgeCell>
        ),
        grow: 1,
      });
    }

    return columns;
  }, [
    activoFijo,
    revaluos,
    restricciones,
    activoFijoLibre,
    creditoMaximoUnidades,
    creditoMaximoPesos,
    labelUnidad,
    hideCalificacion,
    calificacion_.label,
    calificacion_.variant,
  ]);

  return (
    <Card margin={{ bottom: '5' }}>
      <Card variant={clientTheme ? 'primary' : 'success'} modifier='weak' spacing={'2'}>
        <H type='4' variant='neutral-1' withMargin={false} weight='bold' modifier='strong'>
          Crédito máximo a otorgar
        </H>
      </Card>
      {!readOnly && (
        <Card spacing={'2'} margin={{ top: '3', bottom: '2' }}>
          <P type='2' variant='neutral' withMargin={false}>
            Completá los valores solicitados y tendrás el cálculo del crédito máximo a otorgar.
          </P>
        </Card>
      )}
      {readOnly ? (
        <div style={{ padding: '0 8px', marginTop: '12px' }}>
          <Table
            columns={readOnlyColumns}
            data={[{ id: 'credito-maximo-row' }]}
            tableData={{ size: 1, totalElements: 1 }}
            variant='weak'
            disablePagination
          />
        </div>
      ) : (
        <Card spacing={'2'}>
          <FieldsGrid $columns={hideCalificacion ? 6 : 7}>
            <Label type='4' modifier='strong'>Activo Fijo</Label>
            <Label type='4' modifier='strong' id='revaluos'>Revaluos *</Label>
            <Label type='4' modifier='strong' id='restricciones'>Restricciones *</Label>
            <Label type='4' modifier='strong'>Activo Fijo Libre</Label>
            <Label type='4' modifier='strong'>{labelUnidad}</Label>
            <Label type='4' modifier='strong'>Crédito Máximo $</Label>
            {!hideCalificacion && <Label type='4' modifier='strong'>Calificación</Label>}

            <FieldValue>
              <Span type='3' variant='neutral'>$ {formatCurrency(activoFijo)}</Span>
            </FieldValue>

            <FieldValue>
              <Input
                id='revaluos'
                value={revaluos}
                placeholder='0'
                onlyNumbers
                size='md'
                disabled={isRecalculating}
                onChange={(e) => onRevaluosChange(e.target.value)}
                onBlur={onBlur}
              />
            </FieldValue>

            <FieldValue>
              <Input
                id='restricciones'
                value={restricciones}
                placeholder='0'
                onlyNumbers
                size='md'
                disabled={isRecalculating}
                onChange={(e) => onRestriccionesChange(e.target.value)}
                onBlur={onBlur}
              />
            </FieldValue>

            <FieldValue>
              {isRecalculating ? (
                <Spinner size='sm' variant='primary' />
              ) : (
                <Span type='3' variant='neutral'>$ {formatCurrency(activoFijoLibre)}</Span>
              )}
            </FieldValue>

            <FieldValue>
              {isRecalculating ? (
                <Spinner size='sm' variant='primary' />
              ) : (
                <Span type='3' variant='neutral'>{formatCurrency(creditoMaximoUnidades)}</Span>
              )}
            </FieldValue>

            <FieldValue>
              {isRecalculating ? (
                <Spinner size='sm' variant='primary' />
              ) : (
                <Span type='3' variant='neutral'>$ {formatCurrency(creditoMaximoPesos)}</Span>
              )}
            </FieldValue>

            {!hideCalificacion && (
              <FieldValue>
                {isRecalculating ? (
                  <Spinner size='sm' variant='primary' />
                ) : (
                  <Badge type='text' variant={calificacion_.variant} size='sm'>
                    {calificacion_.label}
                  </Badge>
                )}
              </FieldValue>
            )}
          </FieldsGrid>
        </Card>
      )}
    </Card>
  );
};

export default ActivoFijoLibreForm;
